import React, {useMemo} from "react";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Message} from "primereact/message";
import {Skeleton} from "primereact/skeleton";
import {Chart} from "primereact/chart";
import {buildReportPreview, chartOptions} from "./dynamic-report-builder-dialog";

function ReportCardPreview({report, reportDataContext, translate}) {
    const {config, preview, validationErrors} = useMemo(() => {
        if (report.system || !report.config) return {config: report.config, preview: null, validationErrors: []};
        return buildReportPreview(report.config, reportDataContext, translate);
    }, [report, reportDataContext, translate]);

    if (report.system || !report.config) return null;

    if (reportDataContext.loading) {
        return <Skeleton width="100%" height="10rem" className="mt-3"/>;
    }

    if (reportDataContext.error) {
        return <Message severity="warn" text={translate("reports.builder.errorPreview")} className="mt-3 w-full text-xs"/>;
    }

    if (validationErrors.length) {
        return <Message severity="info" text={validationErrors[0]} className="mt-3 w-full text-xs"/>;
    }

    if (preview?.kind === "empty") {
        return (
            <div className="mt-3 h-10rem flex flex-column align-items-center justify-content-center gap-2 text-center surface-50 border-1 border-200 border-round p-3">
                <i className="pi pi-database text-xl text-500"/>
                <span className="text-sm text-700">{translate("reports.builder.emptyPreview.title")}</span>
                <small className="text-xs text-500">{translate("reports.builder.emptyPreview.description")}</small>
            </div>
        );
    }

    if (preview?.kind === "number") {
        return (
            <div className="mt-3 h-10rem flex flex-column align-items-center justify-content-center gap-2 text-center surface-50 border-1 border-200 border-round p-3">
                <span className="text-5xl font-bold text-primary">{preview.value}</span>
                <small className="text-xs text-600">{preview.label}</small>
            </div>
        );
    }

    if (preview?.kind === "chart") {
        return (
            <div className="mt-3 h-12rem w-full">
                <Chart
                    type={preview.type}
                    data={preview.data}
                    options={chartOptions(config, translate)}
                    className="w-full h-full"
                />
            </div>
        );
    }

    return null;
}

function ReportCard({report, reportDataContext, translate, onEdit}) {
    const header = (
        <div className="flex align-items-center justify-content-between gap-2 p-3 pb-0">
            {onEdit ? (
                <Button
                    icon="pi pi-pencil"
                    text
                    rounded
                    size="small"
                    severity="secondary"
                    aria-label={translate("reports.builder.editReport")}
                    onClick={(event) => {
                        event.stopPropagation();
                        onEdit(report);
                    }}
                />
            ) : <span/>}
            <span className="inline-flex align-items-center justify-content-center w-3rem h-3rem border-round surface-100 text-primary border-1 border-primary">
                <i className={`${report.icon} text-xl`}/>
            </span>
        </div>
    );

    const footer = (
        <div className="flex align-items-center justify-content-between gap-2 pt-2 border-top-1 border-200 text-xs text-500">
            <span>{report.time}</span>
            <span>{report.author}</span>
            <span className="inline-flex align-items-center justify-content-center w-2rem h-2rem border-circle surface-200 text-700 text-xs">
                {translate("reports.page.youBadge")}
            </span>
        </div>
    );

    return (
        <Card
            className={`h-full border-1 border-200 shadow-none ${onEdit ? "cursor-pointer" : ""}`}
            header={header}
            footer={footer}
            onClick={onEdit ? () => onEdit(report) : undefined}
        >
            <div className="flex flex-column gap-2 text-right">
                <h3 className="m-0 text-base font-medium text-900">{report.title}</h3>
                <p className="m-0 text-sm line-height-3 text-700">{report.description}</p>
            </div>
            <ReportCardPreview report={report} reportDataContext={reportDataContext} translate={translate}/>
        </Card>
    );
}

export default ReportCard;
